import { useId } from "react";

import { RanaLoading } from "@nens/ranalib";

import { LayerControl, LayerControlHeader } from "@/components/layer-control";
import { RanaMap } from "@/lib/rana-map";
import { RanaPopup } from "@/lib/rana-popup/rana-popup";
import { RanaRasterDetails } from "@/lib/rana-raster-details/rana-raster-details";
import { RasterFileDescriptor } from "@/types/descriptor-types";

import { useRasterPreview } from "../hooks/use-raster-preview";

interface ProjectFileRasterPreviewProps {
  descriptor: RasterFileDescriptor;
  title: string;
}

export function ProjectFilePreviewRaster({ descriptor, title }: ProjectFileRasterPreviewProps) {
  const mapId = `raster-map-${useId()}`;

  const {
    isPending,
    isSuccess,
    bounds,
    layer,
    styles,
    tiles,
    unit,
    visibleLayers,
    setVisibleLayers,
    point,
    setPoint,
    setDefaultColor,
    setColorRange,
    setColorBounds,
    setOpacity,
    opacity,
  } = useRasterPreview(descriptor, title);

  if (isPending) return <RanaLoading size="xl" color="brand.clear_creek" />;
  if (!isSuccess || !layer) return null;

  return (
    <RanaMap
      mapId={mapId}
      bounds={bounds}
      mapProps={{
        onClick: (e) => setPoint(e.lngLat),
      }}
    >
      <RanaMap.MapScale />

      <RanaMap.ControlsTopLeft>
        <LayerControlHeader />
        <LayerControl
          descriptorId={descriptor.id}
          title={title}
          layers={[title]}
          visibleLayers={visibleLayers}
          setVisibleLayers={setVisibleLayers}
        />
      </RanaMap.ControlsTopLeft>
      <RanaMap.ControlsBottomLeft overflow="hidden" minW={52} py={2}>
        <RanaRasterDetails
          descriptor={descriptor}
          layer={layer}
          styles={styles}
          unit={unit}
          setColorBounds={setColorBounds}
          setDefaultColor={setDefaultColor}
          setColorRange={setColorRange}
          setOpacity={setOpacity}
          opacity={opacity}
        />
      </RanaMap.ControlsBottomLeft>

      {/* Raster layer */}
      <RanaMap.Source bounds={bounds} type="raster" tiles={[tiles]}>
        <RanaMap.Layer
          id={`raster-layer-${layer}`}
          type="raster"
          paint={{ "raster-opacity": opacity }}
          isVisible={!!visibleLayers.length}
        />
      </RanaMap.Source>

      {/* Popup Info */}
      <RanaPopup point={point} onClose={() => setPoint(null)}>
        <RanaPopup.RasterInfo
          layer={layer}
          styleId={styles}
          unit={unit}
          descriptorId={descriptor.id}
          point={point}
        />
      </RanaPopup>
    </RanaMap>
  );
}
